// P3-007: Battle runtime state store

import { create } from 'zustand';
import type { PrepareResult, ContractResult, DeployedAgent } from '@/shared/types/phase-contracts';
import type {
  BattleAgent,
  ActiveBuff,
  EnemyState,
  BattleRuntimeStats,
} from '../types/battle-internal';
import { ENEMY_TYPE_CONFIGS } from '../data/enemy-configs';

export interface BattleStoreState {
  // Core state
  projectName: string;
  team: BattleAgent[];
  enemy: EnemyState | null;
  currentTurn: number;
  maxTurns: number; // = deadline
  buffs: ActiveBuff[];
  combo: number;
  result: 'VICTORY' | 'DEFEAT' | null;
  overloadLevel: 'none' | 'light' | 'medium' | 'heavy';

  // Statistics
  stats: BattleRuntimeStats;

  // Actions
  init: (prepare: PrepareResult, contract: ContractResult) => void;
  incrementTurn: () => void;
  setCombo: (combo: number) => void;
  dealDamage: (amount: number) => void;
  addBuff: (buff: ActiveBuff) => void;
  tickBuffs: () => void;
  setAgentSkillCD: (agentId: string, cd: number) => void;
  decrementAllCDs: () => void;
  disableAgentSkill: (agentId: string) => void;
  enableAllAgentSkills: () => void;
  extendDeadline: (turns: number) => void;
  reduceDeadline: (turns: number) => void;
  setEnemyPhase: (phase: EnemyState['phase']) => void;
  setDemandChangeTurns: (turns: number) => void;
  decrementDemandChangeTurns: () => void;
  setEnemyThresholdTriggered: (threshold: '66' | '33') => void;
  setResult: (result: 'VICTORY' | 'DEFEAT') => void;
  updateStats: (update: Partial<BattleRuntimeStats>) => void;
  incrementStat: (key: keyof BattleRuntimeStats, amount?: number) => void;
  reset: () => void;
}

function deployedToBattleAgent(agent: DeployedAgent): BattleAgent {
  return {
    ...agent,
    currentSkillCD: agent.activeSkill.cooldown,
    skillDisabledThisTurn: false,
  };
}

const initialStats: BattleRuntimeStats = {
  totalDamageDealt: 0,
  maxCombo: 0,
  critCount: 0,
  overloadEvents: 0,
  skillsTriggered: 0,
};

export const useBattleStore = create<BattleStoreState>()((set, get) => ({
  projectName: 'PROJECT',
  team: [],
  enemy: null,
  currentTurn: 0,
  maxTurns: 0,
  buffs: [],
  combo: 0,
  result: null,
  overloadLevel: 'none',
  stats: { ...initialStats },

  init: (prepare: PrepareResult, contract: ContractResult) => {
    const { project } = contract;
    const enemyConfig = ENEMY_TYPE_CONFIGS[project.type];
    const hpMultiplier = enemyConfig?.hpMultiplier ?? 1.0;
    const maxHp = Math.round(project.workload * hpMultiplier);

    set({
      projectName: project.name || 'PROJECT',
      team: prepare.team.map(deployedToBattleAgent),
      enemy: {
        hp: maxHp,
        maxHp,
        phase: 'normal',
        type: project.type,
        triggered66: false,
        triggered33: false,
        demandChangeTurnsRemaining: 0,
      },
      currentTurn: 0,
      maxTurns: project.deadline,
      buffs: [],
      combo: 0,
      result: null,
      overloadLevel: prepare.hardwareSnapshot.overloadLevel,
      stats: { ...initialStats },
    });
  },

  incrementTurn: () => {
    set((state) => ({ currentTurn: state.currentTurn + 1 }));
  },

  setCombo: (combo: number) => {
    const { stats } = get();
    const newMaxCombo = Math.max(stats.maxCombo, combo);
    set({
      combo,
      stats: { ...stats, maxCombo: newMaxCombo },
    });
  },

  dealDamage: (amount: number) => {
    const { enemy, stats } = get();
    if (!enemy) return;

    const newHp = Math.max(0, enemy.hp - amount);
    set({
      enemy: { ...enemy, hp: newHp },
      stats: { ...stats, totalDamageDealt: stats.totalDamageDealt + amount },
    });
  },

  addBuff: (buff: ActiveBuff) => {
    set((state) => ({ buffs: [...state.buffs, buff] }));
  },

  tickBuffs: () => {
    set((state) => ({
      buffs: state.buffs
        .map((b) => ({ ...b, remainingTurns: b.remainingTurns - 1 }))
        .filter((b) => b.remainingTurns > 0),
    }));
  },

  setAgentSkillCD: (agentId: string, cd: number) => {
    set((state) => ({
      team: state.team.map((a) =>
        a.agentId === agentId ? { ...a, currentSkillCD: cd } : a,
      ),
    }));
  },

  decrementAllCDs: () => {
    set((state) => ({
      team: state.team.map((a) => ({
        ...a,
        currentSkillCD: Math.max(0, a.currentSkillCD - 1),
      })),
    }));
  },

  disableAgentSkill: (agentId: string) => {
    set((state) => ({
      team: state.team.map((a) =>
        a.agentId === agentId ? { ...a, skillDisabledThisTurn: true } : a,
      ),
    }));
  },

  enableAllAgentSkills: () => {
    set((state) => ({
      team: state.team.map((a) => ({ ...a, skillDisabledThisTurn: false })),
    }));
  },

  extendDeadline: (turns: number) => {
    set((state) => ({ maxTurns: state.maxTurns + turns }));
  },

  reduceDeadline: (turns: number) => {
    set((state) => ({ maxTurns: Math.max(state.currentTurn, state.maxTurns - turns) }));
  },

  setEnemyPhase: (phase: EnemyState['phase']) => {
    const { enemy } = get();
    if (!enemy) return;
    set({ enemy: { ...enemy, phase } });
  },

  setDemandChangeTurns: (turns: number) => {
    const { enemy } = get();
    if (!enemy) return;
    set({ enemy: { ...enemy, demandChangeTurnsRemaining: turns } });
  },

  decrementDemandChangeTurns: () => {
    const { enemy } = get();
    if (!enemy || enemy.demandChangeTurnsRemaining <= 0) return;
    set({ enemy: { ...enemy, demandChangeTurnsRemaining: enemy.demandChangeTurnsRemaining - 1 } });
  },

  setEnemyThresholdTriggered: (threshold: '66' | '33') => {
    const { enemy } = get();
    if (!enemy) return;
    if (threshold === '66') {
      set({ enemy: { ...enemy, triggered66: true } });
    } else {
      set({ enemy: { ...enemy, triggered33: true } });
    }
  },

  setResult: (result: 'VICTORY' | 'DEFEAT') => {
    set({ result });
  },

  updateStats: (update: Partial<BattleRuntimeStats>) => {
    set((state) => ({ stats: { ...state.stats, ...update } }));
  },

  incrementStat: (key: keyof BattleRuntimeStats, amount = 1) => {
    set((state) => ({
      stats: { ...state.stats, [key]: state.stats[key] + amount },
    }));
  },

  reset: () => {
    set({
      projectName: 'PROJECT',
      team: [],
      enemy: null,
      currentTurn: 0,
      maxTurns: 0,
      buffs: [],
      combo: 0,
      result: null,
      overloadLevel: 'none',
      stats: { ...initialStats },
    });
  },
}));
